/**
 * Login Page
 * Uses Google for approved users and an emergency local-password path for administrators.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/auth-context';
import { loginSchema, type LoginFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';

function GoogleMark() {
  return (
    <svg aria-hidden="true" className="size-5 shrink-0" viewBox="0 0 48 48">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.35 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.12 24.55c0-1.64-.15-3.22-.43-4.73H24v8.95h12.42c-.54 2.88-2.16 5.32-4.6 6.95v5.64h7.46c4.36-4.02 6.84-9.94 6.84-16.81z"
      />
      <path
        fill="#FBBC05"
        d="M10.54 28.59c-.5-1.48-.78-3.06-.78-4.68s.28-3.2.78-4.68v-5.64H2.56A23.997 23.997 0 000 24c0 3.87.93 7.53 2.56 10.41l7.98-5.82z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.46-5.64c-2.13 1.43-4.86 2.28-8.44 2.28-6.26 0-11.57-4.22-13.46-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const googleError = new URLSearchParams(window.location.search).get('error') === 'google';
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema) as Resolver<LoginFormData>,
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.password);
    } catch {
      // The auth context displays the error.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">FAME Backoffice</CardTitle>
          <CardDescription>Meld je aan met het Google-account dat vooraf is goedgekeurd.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {googleError && (
            <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Google-aanmelding is niet gelukt of dit account heeft geen toegang.
            </p>
          )}
          <Button
            className="h-11 w-full border-neutral-300 bg-white text-base font-medium text-neutral-700 shadow-sm hover:border-neutral-400 hover:bg-neutral-50 hover:text-neutral-900"
            onClick={() => window.location.assign('/api/auth/google/start')}
            type="button"
            variant="outline"
          >
            <GoogleMark />
            Inloggen met Google
          </Button>
          {!showPasswordLogin && (
            <div className="border-t pt-4">
              <Button className="w-full" type="button" variant="outline" onClick={() => setShowPasswordLogin(true)}>
                Administrator: aanmelden met wachtwoord
              </Button>
            </div>
          )}
          {showPasswordLogin && (
            <Form {...form}>
              <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mailadres</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="email" disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Noodwachtwoord</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" disabled={isSubmitting} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Inloggen...' : 'Inloggen'}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
